import { flow, types } from 'mobx-state-tree';
import { Messenger } from 'http-requests';
import { SearchMessageDTO } from 'http-requests/messenger';
import { RequestStateModel } from 'store/models/general-models';
import { RequestStatusMST } from 'store/models/requestsStore';
import { MessageSourceMST } from '../../constants';

export const SearchResultMessageModel = types
  .model('SearchResultMessage', {
    id: types.optional(types.identifier, ''),
    content: types.optional(types.string, ''),
    user_id: types.optional(types.maybeNull(types.string), null),
    user_number: types.optional(types.maybeNull(types.string), null),
    user_full_name: types.optional(types.maybeNull(types.string), null),
    user_provider_id: types.optional(types.maybeNull(types.string), null),
    user_provider_name: types.optional(types.maybeNull(types.string), null),
    user_position: types.optional(types.maybeNull(types.string), null),
    created_at: types.optional(types.maybeNull(types.string), null),
    conversation_id: types.optional(types.string, ''),
    source: types.optional(types.maybeNull(MessageSourceMST), null),
    attached_files_ids: types.optional(types.array(types.string), []),
    is_removed: types.optional(types.boolean, false),

    request_id: types.optional(types.string, ''),
    request_number: types.optional(types.string, ''),
    request_address: types.optional(types.string, ''),
    request_status: types.optional(RequestStatusMST, 'accepted'),
    request_category: types.optional(types.string, ''),

    searchResultId: types.optional(types.string, ''), // `${conversation_id}:${message_id}`
  })
  .views((self) => ({
    get lastMessage() {
      return self;
    },
  }));

export const SearchResultModel = types
  .model('SearchResult', {
    offset: types.optional(types.number, 0),
    limit: types.optional(types.number, 20),
    isArchived: types.optional(types.boolean, false),
    isSearching: types.optional(types.boolean, false),
    searchQuery: types.optional(types.string, ''),
    messages: types.optional(types.array(SearchResultMessageModel), []),
    hasMoreSearchResults: types.optional(types.boolean, true),
  })
  .volatile(() => ({
    searchMessagesStatus: RequestStateModel.create(),
  }))
  .views((self) => ({
    get existingMessagesIds() {
      return new Set(self.messages.map((m) => m.id));
    },
  }))
  .actions((self) => ({
    setSearchQuery(query: string) {
      self.searchQuery = query;
    },
    enterSearchMode() {
      self.isSearching = true;
    },
    changeListType(value?: boolean) {
      self.isArchived = value ?? !self.isArchived;
    },
    addMessages(messages: SearchMessageDTO[]) {
      const newMessages = messages
        .filter((message) => !self.existingMessagesIds.has(message.id))
        .map((message) =>
          SearchResultMessageModel.create({
            ...message,
            searchResultId: `${message.conversation_id}:${message.id}`,
          })
        );

      self.messages.replace([...self.messages, ...newMessages]);
    },
    exitSearchMode() {
      self.isSearching = false;
      self.searchQuery = '';
      self.messages.clear();
      self.offset = 0;
      self.hasMoreSearchResults = true;
    },
    clearSearchResults() {
      self.messages.clear();
      self.offset = 0;
      self.hasMoreSearchResults = true;
    },
  }))
  .actions((self) => ({
    searchMessages: flow(function* ({ loadMore }: { loadMore?: boolean } = {}) {
      if (!self.searchQuery.trim()) {
        return;
      }

      const initialOffset = self.offset;

      if (loadMore) {
        self.offset += self.limit;
      }

      try {
        self.searchMessagesStatus.loadingStates();
        const response: SearchMessageDTO[] =
          yield Messenger.GetConversationsBySubstring({
            limit: self.limit,
            offset: self.offset,
            substr: self.searchQuery,
            is_archived: self.isArchived,
          });

        self.addMessages(response);
        self.hasMoreSearchResults = response.length >= self.limit;
        self.searchMessagesStatus.successStates();

        return self.messages;
      } catch (error) {
        self.searchMessagesStatus.errorStates();
        console.error(error);
        self.offset = initialOffset;
      } finally {
        self.searchMessagesStatus.finallyStates();
      }
    }),
  }));
